"use client"

import { useEffect, useState } from "react"
import { FiCheck, FiChevronRight } from "react-icons/fi"
import CreateMatch from "./steps/create-match"
import SelectPlayers from "./steps/select-players"
import PreviewRules from "./steps/preview-rules"
import ConductToss from "./steps/conduct-toss"
import StartInnings from "./steps/start-innings"
import api from "../../libs/axios"
import { DetailsLoader } from "../../utils/loader"

const steps = [
    { id: 1, title: "Setup", description: "Basic info" },
    { id: 2, title: "Players", description: "Choose teams" },
    { id: 3, title: "Rules", description: "Configure rules" },
    { id: 4, title: "Toss", description: "Toss selection" },
    { id: 5, title: "Start", description: "Begin innings" },
]

export default function MatchStepper({ matchId }: { matchId?: number | string }) {
    const [loading, setLoading] = useState(false)
    const [currentStep, setCurrentStep] = useState(1)
    const [matchData, setMatchData] = useState<any>({})
    const [completedSteps, setCompletedSteps] = useState<number[]>([])

    const fetchMatchData = async () => {
        if (!matchId) return;
        setLoading(true);
        try {
            const res = await api.get(`/matches/${matchId}`);
            setMatchData((prev: any) => ({ ...prev, match_id: res.data.match.id, ...res.data.match }));
        } catch (err) {
            console.error("Error fetching match data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMatchData();
    }, [])

    const handleStepComplete = (stepNumber: number, data: any) => {
        setMatchData((prev: any) => ({ ...prev, ...data }))
        setCompletedSteps((prev) => [...prev.filter((s) => s !== stepNumber), stepNumber])
        if (stepNumber < 5) {
            setCurrentStep(stepNumber + 1)
        }
    }

    const handleStepBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1)
        }
    }

    if (loading) {
        return (
            <DetailsLoader />
        )
    }

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <CreateMatch onComplete={(data) => handleStepComplete(1, data)} matchData={matchData} />
            case 2:
                return (
                    <SelectPlayers
                        onComplete={(data) => handleStepComplete(2, data)}
                        onBack={handleStepBack}
                        matchData={matchData}
                    />
                )
            case 3:
                return (
                    <PreviewRules
                        onComplete={(data) => handleStepComplete(3, data)}
                        onBack={handleStepBack}
                        matchData={matchData}
                        onEditBasicInfo={() => setCurrentStep(1)}
                    />
                )
            case 4:
                return (
                    <ConductToss
                        onComplete={(data) => handleStepComplete(4, data)}
                        onBack={handleStepBack}
                        matchData={matchData}
                    />
                )
            case 5:
                return (
                    <StartInnings
                        onComplete={(data) => handleStepComplete(5, data)}
                        onBack={handleStepBack}
                        matchData={matchData}
                    />
                )
            default:
                return null
        }
    }

    return (
        <div className="w-full bg-white rounded-lg shadow border">
            <div className="px-4 sm:px-6 lg:px-8 py-4 border-b border-gray-200">
                <div className="w-full">
                    {/* Mobile Layout - Show only current step */}
                    <div className="block md:hidden">
                        {steps
                            .filter(step => step.id === currentStep)
                            .map((step) => (
                                <div key={step.id} className="flex items-center">
                                    {/* Step circle */}
                                    <div className="flex-shrink-0">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium ${completedSteps.includes(step.id) ? "bg-green-500 text-white" : currentStep === step.id ? "bg-primary-500 text-white" : "bg-gray-300 text-gray-600"} `}>
                                            {completedSteps.includes(step.id) ? <FiCheck className="w-5 h-5" /> : step.id}
                                        </div>
                                    </div>

                                    {/* Step content */}
                                    <div className="ml-4 min-w-0 flex-1">
                                        <p className="text-lg font-semibold text-primary-600">
                                            {step.title}
                                        </p>
                                        <p className="text-sm text-gray-500 mt-1">{step.description}</p>
                                    </div>
                                </div>
                            ))
                        }

                        {/* Progress indicator */}
                        <div className="mt-4 flex items-center justify-center space-x-2">
                            {steps.map((step) => (
                                <div key={step.id} className={` w-2 h-2 rounded-full ${step.id === currentStep ? "bg-primary-500" : completedSteps.includes(step.id) ? "bg-green-500" : "bg-gray-300"}`} />))}
                        </div>
                    </div>

                    {/* Desktop Layout - Horizontal with arrows */}
                    <div className="hidden md:block">
                        <div className="flex items-center justify-between">
                            {steps.map((step, index) => (
                                <div key={step.id} className="flex items-center flex-1">
                                    {/* Step content */}
                                    <div className="flex flex-col items-center text-center flex-1">
                                        {/* Step circle */}
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg lg:text-xl font-medium mb-1 ${completedSteps.includes(step.id) ? "bg-green-500 text-white" : currentStep === step.id ? "bg-primary-500 text-white" : "bg-gray-300 text-gray-600"}`}>
                                            {completedSteps.includes(step.id) ? <FiCheck className="w-6 h-6 lg:w-7 lg:h-7" /> : step.id}
                                        </div>

                                        {/* Step title and description */}
                                        <div className="max-w-32 lg:max-w-40">
                                            <p className="text-xs xl:text-sm text-gray-500 leading-relaxed">
                                                {step.description}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Arrow between steps */}
                                    {index < steps.length - 1 && (
                                        <div className="flex items-center justify-center px-4 xl:px-6">
                                            <FiChevronRight className="w-5 h-5 xl:w-6 xl:h-6 text-gray-400" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Step Content */}
            <div className="p-6">{renderStep()}</div>
        </div>
    )
}
